import {fetchAuthSession, JWT} from 'aws-amplify/auth';
import {redirect} from "react-router-dom";
import {v4 as uuidv4} from 'uuid';
import {TraceService} from "../service/TraceService";
import {TraceType} from "../model/user/general/Trace";
import Clarity from "@microsoft/clarity";
import {generateSessionIdIfNotExists, getSessionId} from "./TrackingUtil";

const ADMIN_GROUP = process.env.REACT_APP_ADMIN_GROUP!!;

const ASSISTANT_GROUP = "Assistant";

const EXERCISE_REVIEWER_GROUP = "Exercise_Reviewer";

const EFFECTIVE_ACTION_COURSE_SUBSCRIBER = "Effective_Action_Course_Subscriber";

const EFFECTIVE_ACTION_MINI_COURSE_SUBSCRIBER = "Effective_Action_Mini_Course_Subscriber";

const UNDERSTAND_TEENAGER_COURSE_SUBSCRIBER = "Understanding_Teenager_Course_Subscriber";

const SPC_COURSE_SUBSCRIBER = "SPC_Course_Subscriber";

export interface TokenPayload {
    aud: string
    auth_time: number
    'cognito:groups': string[]
    'cognito:username': string
    'custom:confirmationCode': string
    email: string
    email_verified: boolean
    event_id: string
    exp: number
    iat: number
    iss: string
    jti: string
    name: string
    origin_jti: string
    sub: string
    token_use: string
}

async function getRawIdToken(): Promise<JWT | null> {
    try {
        const {idToken} = (await fetchAuthSession()).tokens ?? {};

        return idToken ? idToken : null;
    } catch (err) {
        console.log(err);
        return null;
    }
}

export async function getRawIdTokenAsString(): Promise<string> {
    try {
        const idToken = await getRawIdToken()

        return idToken ? idToken.toString() : '';
    } catch (err) {
        console.log(err);
        return '';
    }
}

export async function getRawIdTokenWithGroups(): Promise<[string, string[]]> {
    try {
        const idToken = await getRawIdToken();

        if (!idToken) {
            return ['', []];
        }

        const groups = idToken.payload['cognito:groups'];

        try {
            Clarity.identify(idToken.payload['sub'] ?? uuidv4(), getSessionId() ?? undefined);
        } catch (error) {
        }

        if (!groups) {
            return [idToken.toString(), []];
        }

        return [idToken.toString(), groups as string[]];
    } catch (err) {
        console.log(err);
        return ['', []];
    }
}

export async function tokenLoader({request}: { request: Request }): Promise<[string, string[]]> {
    const url = new URL(request.url);
    const pathname = url.pathname;

    // 1) Nie obsługuj błędu na stronie docelowej, żeby nie zrobić pętli
    if (!pathname.startsWith("/login-error")) {
        // 2) Pobierz error_description z query i/lub hash
        const fromSearch = url.searchParams.get("error_description");
        const fromHash = (() => {
            const h = url.hash.replace(/^#\??/, ""); // usuń # lub #?
            if (!h) return null;
            return new URLSearchParams(h).get("error_description");
        })();

        let error = fromSearch || fromHash;

        if (error) {
            // 3) Normalizacja: + -> spacje; dekodowanie UTF-8
            try {
                error = decodeURIComponent(error.replace(/\+/g, " "));
            } catch {
            }

            const known = "Użytkownik o podanym adresie email istnieje";
            const target = error.includes(known)
                ? "/login-error?error_description=" + encodeURIComponent(
                "Użytkownik o podanym adresie email istnieje już w bazie danych. Zaloguj się bezpośrednio przy użyciu adresu email."
            )
                : "/login-error";

            // 4) Redirect na poziomie loadera — brak „białego” migania
            throw redirect(target);
        }
    }

    // Dotychczasowe zachowanie:
    generateSessionIdIfNotExists();
    TraceService.addTrace(TraceType.PageLoad, undefined, request.url);

    return await getRawIdTokenWithGroups();
}

export function loadTokenWithGroupsFromLoaderData(loaderData: any): [string, string[]] {
    let idToken: string;
    let groups: string[];
    if (loaderData) {
        [idToken, groups] = loaderData as [string, string[]];
    } else {
        idToken = '';
        groups = [];
    }

    return [idToken, groups];
}

export async function isLoggedIn(): Promise<boolean> {
    return (await getRawIdTokenAsString()) !== '';
}

export async function redirectIfLoggedIn(): Promise<Response | null> {
    const token = await getRawIdTokenAsString();

    if (token !== '') {
        return redirect('/produkt');
    }
    return null;
}

export async function redirectIfNotLoggedIn(): Promise<Response | null> {
    const token = await getRawIdTokenAsString();

    if (!token) {
        return redirect('/logowanie');
    }
    return null;
}

export async function redirectIfNotAdmin(): Promise<Response | [string, string[]]> {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
        localStorage.setItem('sessionId', uuidv4());
    }
    const tokens = (await fetchAuthSession()).tokens;
    if (!tokens) {
        return redirect('/logowanie');
    }

    const rawGroups = tokens.accessToken.payload['cognito:groups'];

    if (!rawGroups) {
        return redirect('/');
    }

    const groups = rawGroups as string[]

    if (!groups.includes(ADMIN_GROUP) && !groups.includes(ASSISTANT_GROUP)) {
        return redirect('/');
    }

    return await getRawIdTokenWithGroups();
}

export async function redirectIfNotFullAdmin(): Promise<Response | [string, string[]]> {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
        localStorage.setItem('sessionId', uuidv4());
    }
    const tokens = (await fetchAuthSession()).tokens;
    if (!tokens) {
        return redirect('/logowanie');
    }

    const rawGroups = tokens.accessToken.payload['cognito:groups'];

    if (!rawGroups) {
        return redirect('/');
    }

    const groups = rawGroups as string[]

    if (!groups.includes(ADMIN_GROUP)) {
        return redirect('/admin');
    }

    return await getRawIdTokenWithGroups();
}

export async function redirectIfNotAnyCourseSubscriber(): Promise<Response | null> {
    const [_, groups] = await getRawIdTokenWithGroups();
    if (!hasEffectiveActionCourseSubscriberGroup(groups) && !hasEffectiveActionMiniCourseSubscriberGroup(groups)) {
        return redirect('/oferta/kurs-efektywnego-dzialania');
    }
    return null;
}

export async function redirectIfNotEffectiveActionCourseSubscriber(): Promise<Response | null> {
    const [idToken, groups] = await getRawIdTokenWithGroups();

    // If not logged in, redirect to login page
    if (!idToken) {
        return redirect('/logowanie');
    }

    // If logged in but doesn't have access, redirect to offer page
    if (!hasEffectiveActionCourseSubscriberGroup(groups)) {
        return redirect('/oferta/kurs-efektywnego-dzialania');
    }

    return null;
}

export async function redirectIfNotEffectiveActionMiniCourseSubscriber(): Promise<Response | null> {
    const [_, groups] = await getRawIdTokenWithGroups();
    if (!hasEffectiveActionMiniCourseSubscriberGroup(groups)) {
        return redirect('/newsletter/podstawy-efektywnego-dzialania');
    }
    return null;
}

export async function redirectIfNotUnderstandTeenagerCourseSubscriber(): Promise<Response | null> {
    const [_, groups] = await getRawIdTokenWithGroups();
    if (!hasUnderstandTeenagerCourseSubscriberGroup(groups)) {
        return redirect('/oferta/zrozumiec-nastolatka');
    }
    return null;
}

export async function redirectIfNotTimeManagementSubscriber(): Promise<Response | null> {
    const [idToken, groups] = await getRawIdTokenWithGroups();
    if (!idToken) {
        return redirect('/logowanie');
    }
    if (!hasSpcCourseSubscriberGroup(groups)) {
        return redirect('/system-planowania-czasu');
    }
    return null;
}

export async function isAdmin(): Promise<boolean> {
    const [_, groups] = await getRawIdTokenWithGroups();
    return hasAdminGroup(groups);
}

export async function isEffectiveActionCourseSubscriber(): Promise<boolean> {
    const [_, groups] = await getRawIdTokenWithGroups();
    return hasEffectiveActionCourseSubscriberGroup(groups);
}

export async function isEffectiveActionMiniCourseSubscriber(): Promise<boolean> {
    const [_, groups] = await getRawIdTokenWithGroups();
    return hasEffectiveActionMiniCourseSubscriberGroup(groups);
}

export function hasAdminGroup(groups: string[]): boolean {
    return groups.includes(ADMIN_GROUP);
}

export function hasAssistantGroup(groups: string[]): boolean {
    return groups.includes(ASSISTANT_GROUP);
}

export function hasExerciseReviewerGroup(groups: string[]): boolean {
    return groups.includes(EXERCISE_REVIEWER_GROUP);
}

export function hasEffectiveActionCourseSubscriberGroup(groups: string[]): boolean {
    return groups.includes(EFFECTIVE_ACTION_COURSE_SUBSCRIBER);
}

export function hasEffectiveActionMiniCourseSubscriberGroup(groups: string[]): boolean {
    return groups.includes(EFFECTIVE_ACTION_MINI_COURSE_SUBSCRIBER);
}

export function hasUnderstandTeenagerCourseSubscriberGroup(groups: string[]): boolean {
    return groups.includes(UNDERSTAND_TEENAGER_COURSE_SUBSCRIBER);
}

export function hasSpcCourseSubscriberGroup(groups: string[]): boolean {
    return groups.includes(SPC_COURSE_SUBSCRIBER);
}

export async function getCurrentlyLoggedInUser(): Promise<TokenPayload | null> {
    // otherwise it returns null on first call...
    await new Promise(resolve => setTimeout(resolve, 1));
    const session = await fetchAuthSession();
    return session ? (session.tokens?.idToken?.payload as unknown as TokenPayload) : null;
}

export function getGroupsFromAuthSession(session: any): string[] {
    return session.tokens.idToken.payload['cognito:groups'];
}